/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { createFilter } from 'react-select';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { user } from '../../redux/user';
import utils from '../../utils/utils';
import Select from '../../components/form/Select';
import CustomNumberInput from '../../components/CustomNumberInput/CustomNumberInput';
import { GuaranteedIcon } from '../../components/icons';
import Tooltip from '../../components/Tooltip/Tooltip';
import MultiDateSelector from '../../components/MultiDateSelector/MultiDateSelector';
import DatePill from '../../components/DatePill/DatePill';
import classifyDateConflict, { SCHEDULE_CONFLICT } from '../../components/DatePill/classifyDateConflict';
import ConflictBadgeRow from '../../components/ConflictBadgeRow/ConflictBadgeRow';
import trashRedIcon from '../../assets/images/trash-red-icon.svg';

const SLOT_LIMIT = 20;
const SLOT_MIN_LIMIT = 1;

const RequestedShiftGroup = ({
	availableShiftStructures,
	errors,
	isFlex,
	showOnlyFirstRowLabels,
	shiftIndex,
	isGlobalCalendar,
	currentFacility,
	units,
	showUnits,
	selectedShiftStructuresIds,
	maxShiftsToCreate,
	checkIfCanAddTodayShiftForStructure,
	getDateConflict,
	allowPastDates = false,
	...props
}) => {
	const { timezone, permissions, permissionsByFacility } = useSelector(user);
	const [shiftStructureOptions, setShiftStructureOptions] = useState([]);
	const [structureIsOrientation, setStructureIsOrientation] = useState(false);
	const [showCalendar, setShowCalendar] = useState(false);

	const { shiftStructureId, unitId, numberOfProviders, resourceType, isGuaranteed, id, dates } = props.shift;
	const unitsSelectFilterConfig = {
		ignoreCase: true,
		ignoreAccents: true,
		matchFrom: 'any',
		stringify: (option) => option.label.toString(),
		trim: true
	};
	const unitOptions = useMemo(() => {
		if (units?.length > 0) {
			const options = units.map((unit) => ({ label: unit.unit_name, value: unit.id }));
			return [{ value: null, label: '- Remove Unit -' }, ...options];
		}

		return [];
	}, [units]);

	const hasGuaranteePermission = useMemo(() => {
		if (isGlobalCalendar) {
			const facilityPermissions = permissionsByFacility[currentFacility];
			return utils.isAuthorizedExact('PORTAL.GUARANTEE', facilityPermissions);
		}
		return utils.isAuthorizedExact('PORTAL.GUARANTEE', permissions);
	}, [permissions, currentFacility, isGlobalCalendar, permissionsByFacility]);

	/* returns unique resource types values for dropdown selection */
	const getResourceTypeOptions = useCallback(() => {
		const added = {};
		const uniqueResourceTypes = [];

		availableShiftStructures.forEach((shiftStructure) => {
			if (!added[shiftStructure.resource_type]) {
				added[shiftStructure.resource_type] = true;
				uniqueResourceTypes.push({
					value: shiftStructure.resource_type,
					label: shiftStructure.resource_type
				});
			}
		});

		return uniqueResourceTypes;
	}, [availableShiftStructures]);

	/* we need to get the new available shift options in the second dropdown every time either the resource type changes
	 * or the available shift structures are changing
	 * */
	useEffect(() => {
		const structures = availableShiftStructures
			.filter((structure) => structure.resource_type === resourceType)
			.map((structure) => {
				const fromTime = moment.utc(structure.from_time, 'HH:mm:ss').format('h:mm A');
				const toTime = moment.utc(structure.to_time, 'HH:mm:ss').format('h:mm A');
				return {
					value: structure.id,
					label: structure.is_orientation ? `Orientation ${fromTime} - ${toTime}` : `${fromTime} - ${toTime}`
				};
			});
		setShiftStructureOptions(structures);
		if (shiftStructureId) {
			const struct = availableShiftStructures.find((structure) => structure.id === shiftStructureId);
			setStructureIsOrientation(struct && struct.is_orientation);
		}
	}, [resourceType, availableShiftStructures, timezone]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateShift = (patchObject) => props.updateShift(shiftIndex, patchObject, id);

	const handleShiftStructureChange = (value) => {
		const _shiftStructure = availableShiftStructures.find((structure) => value.structure === structure.id);
		const _shiftStructureIsOrientation = _shiftStructure ? _shiftStructure.is_orientation : null;
		setStructureIsOrientation(_shiftStructureIsOrientation);
		updateShift({
			shiftStructureId: value.structure,
			isGuaranteed: _shiftStructureIsOrientation ? false : isGuaranteed
		});
	};

	const handleUnitChange = (value) => {
		let unitName = '';
		if (value.unitId) {
			unitName = unitOptions.find((option) => option.value === value.unitId).label;
		}

		updateShift({
			unitId: value.unitId,
			unitName
		});
	};

	/* every time the resource type changes, we need to reset the current shift shiftStructureId */
	const handleResourceTypeChange = (value) => {
		updateShift({
			resourceType: value.type,
			shiftStructureId: null
		});
	};

	const removeShift = () => props.allowRemove && props.removeShift(shiftIndex, id);

	const handleProvidersChange = (currentValue) => {
		let value = null;

		if (Number.isInteger(parseInt(currentValue, 10))) {
			value = parseInt(currentValue, 10);
		}
		updateShift({ numberOfProviders: value });
	};

	const handleIsGuaranteed = () => updateShift({ isGuaranteed: !isGuaranteed });

	const handleBlur = (e) => {
		const parsedValue = parseInt(e.target.value, 10);

		if (!Number.isInteger(parsedValue)) {
			updateShift({ numberOfProviders: null });
		} else if (parsedValue < SLOT_MIN_LIMIT) {
			updateShift({ numberOfProviders: SLOT_MIN_LIMIT });
		}
	};

	const selectorClasses = useMemo(() => {
		return {
			resourceType: classNames('shift-selector flex-1', {
				'showing-error': errors.resourceType
			}),
			shiftStructureId: classNames('shift-selector flex-1', {
				'showing-error': errors.shiftStructureId,
				'showing-unit': showUnits
			}),
			unitId: classNames('shift-selector flex-1', {
				'showing-error': errors.unitId
			}),
			numberOfProviders: classNames('shift-selector', 'provider-selector', {
				'showing-error': errors.numberOfProviders,
				disabled: !shiftStructureId
			})
		};
	}, [shiftIndex, errors, showUnits]);

	const guaranteedTooltipText = useMemo(() => {
		if (props.isHybrid) {
			return 'Guaranteed Shift: Guaranteed Shifts don’t apply to Flex professionals. By guaranteeing a shift, you agree to pay the ShiftMed professional even if you cancel. Guaranteed Shifts are picked up more often.';
		}

		return 'Guaranteed Shift';
	}, [props.isHybrid]);

	const maxAvailableDates = useMemo(
		() =>
			props.shiftsGroups.reduce((acc, cur) => {
				if (cur.id !== id) {
					return acc - cur.dates.length;
				}
				return acc;
			}, maxShiftsToCreate),
		[props.shiftsGroups, id]
	);

	const onOpenCalendar = (e) => {
		e.preventDefault();
		setShowCalendar(true);
	};

	const onChangeDates = (selectedDates) => {
		const dateStrings = selectedDates.sort((a, b) => a - b).map((d) => moment(d).format('YYYY-MM-DD'));
		const hasTodayAsInvalid = errors.invalidDate && dateStrings[0] === moment().format('YYYY-MM-DD');
		updateShift({ dates: dateStrings, invalidDate: hasTodayAsInvalid });
		setShowCalendar(false);
	};

	const removeDate = (ix) => {
		const _dates = [...dates];
		_dates.splice(ix, 1);
		/* set invalidDate to true is error and first element of the array to clear the error */
		updateShift({ dates: _dates, invalidDate: errors.invalidDate && !!ix });
	};

	const minCalendarDate = useMemo(() => {
		if (isFlex && allowPastDates) {
			const sixMonthsAgo = new Date();
			sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
			return sixMonthsAgo;
		}
		if (shiftStructureId && !checkIfCanAddTodayShiftForStructure(shiftStructureId, isFlex, allowPastDates)) {
			const tomorrow = new Date();
			tomorrow.setDate(tomorrow.getDate() + 1);
			return tomorrow;
		}
		return new Date();
	}, [shiftStructureId, allowPastDates, isFlex]);

	const getDateConflictForCalendar = useMemo(
		() => (getDateConflict ? (date) => getDateConflict(shiftStructureId, date) : undefined),
		[getDateConflict, shiftStructureId]
	);

	return (
		<div
			className={classNames('shift-container', {
				'shift-row-grey-background': shiftIndex % 2 !== 0,
				disabled: !currentFacility
			})}
		>
			<div className="shift-options">
				<div className={selectorClasses.resourceType}>
					<Select
						label="Shift type"
						classNamePrefix="select-value"
						className="request-shifts-modal-select"
						name="type"
						value={resourceType}
						hasError={errors.resourceType}
						placeholder="Select..."
						handleChange={handleResourceTypeChange}
						options={getResourceTypeOptions()}
					/>
					<label className={classNames('required-label', { show: errors.resourceType })}>Required</label>
				</div>
				<div className={selectorClasses.shiftStructureId}>
					<Select
						label="Shift time"
						classNamePrefix="select-value"
						className="request-shifts-modal-select"
						name="structure"
						placeholder="Select..."
						value={shiftStructureId}
						hasError={errors.shiftStructureId}
						handleChange={handleShiftStructureChange}
						options={shiftStructureOptions}
						isDisabled={!resourceType}
					/>
					<label className={classNames('required-label', { show: errors.shiftStructureId })}>Required</label>
				</div>
				{showUnits && (
					<div className={selectorClasses.unitId}>
						<Select
							label="Unit"
							classNamePrefix="select-value"
							className="request-shifts-modal-select"
							containerClass="optional-label"
							name="unitId"
							placeholder="Select..."
							value={unitId}
							hasError={errors.unitId}
							handleChange={handleUnitChange}
							options={unitOptions}
							isDisabled={!shiftStructureId}
							isSearchable
							noOptionsMessage="No units found"
							filterFn={createFilter(unitsSelectFilterConfig)}
						/>
					</div>
				)}

				{!props.hideProvidersCount && (
					<div className={selectorClasses.numberOfProviders}>
						<p className="provider-label">Professionals</p>
						<CustomNumberInput
							className={classNames('provider-input', {
								'has-error': errors.numberOfProviders,
								disabled: !shiftStructureId
							})}
							value={numberOfProviders}
							onChange={handleProvidersChange}
							onBlur={handleBlur}
							min={SLOT_MIN_LIMIT}
							max={SLOT_LIMIT}
						/>
						<label
							className={classNames('required-label', {
								show: errors.numberOfProviders
							})}
						>
							Required
						</label>
					</div>
				)}

				{(!isFlex || props.allowRemove) && (
					<div className="actions-container">
						{!isFlex && (
							<>
								<label className="guaranteed-shift">
									<input
										type="checkbox"
										className="guaranteed-checkbox custom-checkbox-control"
										value={isGuaranteed}
										checked={isGuaranteed}
										onChange={handleIsGuaranteed}
										disabled={structureIsOrientation || !hasGuaranteePermission || !shiftStructureId}
									/>
									<Tooltip
										label={guaranteedTooltipText}
										placement="top"
										customClass="request-shifts-guaranteed-tooltip"
									>
										<GuaranteedIcon color="#00B1BE" height={21} width={21} />
									</Tooltip>
								</label>
							</>
						)}
						{props.allowRemove && (
							<div className="remove-section" onClick={removeShift}>
								<img className="remove-img" src={trashRedIcon} alt="remove" />
							</div>
						)}
					</div>
				)}
			</div>

			<div className={classNames('dates-section', { 'date-section-with-remove': props.allowRemove })}>
				<div className="dates-selector-container">
					<div className="dates-selector">
						<div className="dates-selector-label">
							<div>
								Date(s) - <b>{dates.length} selected</b>
							</div>
							<div className="label-right">(MM/DD)</div>
						</div>
						<div className={classNames('input-wrapper', { disabled: !shiftStructureId })}>
							<input
								type="text"
								className={classNames('calendar-trigger-input', { invalid: errors.dates || errors.invalidDate })}
								placeholder="Select all that apply..."
								disabled={!shiftStructureId}
								onClick={onOpenCalendar}
								readOnly
							/>
						</div>
						{errors.dates && <label className="error-message">Required</label>}
						<ConflictBadgeRow errors={errors} />
						{dates.length > 0 && (
							<div className="selected-dates-container">
								{dates.map((dt, ix) => {
									const isoDate = moment(dt).format('YYYY-MM-DD');
									const baseConflict = classifyDateConflict(isoDate, errors);
									const conflictType = baseConflict || (errors.invalidDate && ix === 0 ? SCHEDULE_CONFLICT : null);
									return (
										<DatePill key={isoDate} date={dt} conflictType={conflictType} onRemove={() => removeDate(ix)} />
									);
								})}
							</div>
						)}
					</div>
				</div>
			</div>

			{showCalendar && (
				<MultiDateSelector
					maxDatesCountLimit={maxShiftsToCreate}
					maxAvailableDates={maxAvailableDates}
					minDate={minCalendarDate}
					selectedDates={dates}
					onApply={onChangeDates}
					onClose={() => setShowCalendar(false)}
					getDateConflict={getDateConflictForCalendar}
				/>
			)}
		</div>
	);
};

export default memo(RequestedShiftGroup);
